<template>
    <div :class="itemClasses">
        <div class="detail-content">
            <!-- Ícone -->
            <div class="detail-icon-container" v-if="icon">
                <i :class="iconClasses"></i>
            </div>

            <!-- Label e Value -->
            <div class="detail-text">
                <span class="detail-label">{{ label }}:</span>
                <span :class="valueClasses">{{ displayValue }}</span>
            </div>

            <!-- Badge adicional (opcional) -->
            <div class="detail-badge" v-if="badge">
                <span :class="badgeClasses">{{ badge }}</span>
            </div>
        </div>

        <!-- Tooltip (opcional) -->
        <div class="detail-tooltip" v-if="tooltip">
            <i class="fas fa-info-circle" :title="tooltip"></i>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DetailItem',
    props: {
        label: {
            type: String,
            required: true
        },
        value: {
            type: [String, Number],
            default: 'N/A'
        },
        icon: {
            type: String,
            default: ''
        },
        highlight: {
            type: Boolean,
            default: false
        },
        badge: {
            type: String,
            default: ''
        },
        badgeType: {
            type: String,
            default: 'info', // info, success, warning, danger
            validator: value => ['info', 'success', 'warning', 'danger', 'primary', 'secondary'].includes(value)
        },
        tooltip: {
            type: String,
            default: ''
        },
        size: {
            type: String,
            default: 'normal', // small, normal, large
            validator: value => ['small', 'normal', 'large'].includes(value)
        }
    },
    computed: {
        // Classes do item principal
        itemClasses() {
            return [
                'detail-item',
                `detail-item-${this.size}`,
                {
                    'detail-item-highlight': this.highlight,
                    'detail-item-with-badge': this.badge,
                    'detail-item-with-tooltip': this.tooltip
                }
            ];
        },

        // Classes do ícone
        iconClasses() {
            return [
                this.icon,
                'detail-icon',
                {
                    'icon-highlight': this.highlight
                }
            ];
        },

        // Classes do valor
        valueClasses() {
            return [
                'detail-value',
                {
                    'value-highlight': this.highlight,
                    'value-empty': this.isEmpty
                }
            ];
        },

        // Classes do badge
        badgeClasses() {
            return [
                'badge',
                `badge-${this.badgeType}`
            ];
        },

        // Valor para exibição
        displayValue() {
            if (this.value === null || this.value === undefined || this.value === '') {
                return 'N/A';
            }
            return this.value;
        },

        // Verifica se o valor está vazio
        isEmpty() {
            return this.value === null ||
                this.value === undefined ||
                this.value === '' ||
                this.value === 'N/A';
        }
    }
};
</script>

<style scoped>
.detail-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    margin-bottom: 6px;
    border-radius: 6px;
    transition: all 0.2s ease;
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
}

.detail-item:hover {
    background-color: #e9ecef;
    transform: translateX(2px);
}

.detail-item-highlight {
    background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
    border-color: #2196f3;
    box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
}

.detail-item-highlight:hover {
    background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%);
}

.detail-content {
    display: flex;
    align-items: center;
    flex: 1;
}

.detail-icon-container {
    margin-right: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.detail-icon {
    color: #6c757d;
    font-size: 14px;
    transition: color 0.2s ease;
}

.icon-highlight {
    color: #2196f3;
}

.detail-text {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0; /* Para permitir text-overflow */
}

.detail-label {
    font-weight: 600;
    color: #495057;
    margin-right: 8px;
    white-space: nowrap;
    min-width: fit-content;
}

.detail-value {
    color: #6c757d;
    word-break: break-word;
    flex: 1;
}

.value-highlight {
    color: #2196f3;
    font-weight: 600;
}

.value-empty {
    color: #adb5bd;
    font-style: italic;
}

.detail-badge {
    margin-left: 8px;
}

.badge {
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.badge-info {
    background-color: #17a2b8;
    color: white;
}

.badge-success {
    background-color: #28a745;
    color: white;
}

.badge-warning {
    background-color: #ffc107;
    color: #212529;
}

.badge-danger {
    background-color: #dc3545;
    color: white;
}

.badge-primary {
    background-color: #FE8D18;
    color: white;
}

.badge-secondary {
    background-color: #6c757d;
    color: white;
}

.detail-tooltip {
    margin-left: 8px;
    color: #6c757d;
    cursor: help;
}

.detail-tooltip:hover {
    color: #495057;
}

/* Tamanhos */
.detail-item-small {
    padding: 4px 8px;
    font-size: 12px;
}

.detail-item-small .detail-icon {
    font-size: 12px;
}

.detail-item-small .detail-icon-container {
    width: 20px;
    height: 20px;
    margin-right: 8px;
}

.detail-item-small .badge {
    font-size: 10px;
    padding: 2px 6px;
}

.detail-item-normal {
    padding: 8px 12px;
    font-size: 13px;
}

.detail-item-large {
    padding: 12px 16px;
    font-size: 14px;
}

.detail-item-large .detail-icon {
    font-size: 16px;
}

.detail-item-large .detail-icon-container {
    width: 28px;
    height: 28px;
    margin-right: 12px;
}

.detail-item-large .badge {
    font-size: 12px;
    padding: 4px 10px;
}

/* Responsividade */
@media (max-width: 768px) {
    .detail-item {
        padding: 6px 10px;
        font-size: 12px;
    }

    .detail-icon-container {
        width: 20px;
        height: 20px;
        margin-right: 8px;
    }

    .detail-icon {
        font-size: 12px;
    }

    .detail-label {
        margin-right: 6px;
    }

    .badge {
        font-size: 10px;
        padding: 2px 6px;
    }

    .detail-item-large {
        padding: 8px 12px;
        font-size: 13px;
    }
}

@media (max-width: 576px) {
    .detail-text {
        flex-direction: column;
        align-items: flex-start;
    }

    .detail-label {
        margin-bottom: 2px;
        margin-right: 0;
    }

    .detail-value {
        padding-left: 0;
    }

    .detail-item-with-badge .detail-content {
        flex-direction: column;
        align-items: flex-start;
    }

    .detail-badge {
        margin-left: 0;
        margin-top: 4px;
    }
}

/* Estados especiais */
.detail-item:last-child {
    margin-bottom: 0;
}

.detail-item-with-tooltip:hover .detail-tooltip {
    transform: scale(1.1);
    transition: transform 0.2s ease;
}

/* Animação para itens destacados */
.detail-item-highlight {
    animation: highlight-pulse 2s ease-in-out infinite;
}

@keyframes highlight-pulse {
    0%, 100% {
        box-shadow: 0 2px 4px rgba(33, 150, 243, 0.1);
    }
    50% {
        box-shadow: 0 2px 8px rgba(33, 150, 243, 0.2);
    }
}
</style>



// WEBPACK FOOTER //
// src/components/ReserveVehicles/add-components/DetailItem.vue